import React, { useEffect, useMemo, useState } from 'react';
import { WindowLocation } from '@reach/router';
import styled, { css } from 'styled-components';
import { useABTesting } from '@wec-core/ab-testing';
import {
  Column,
  Grid,
  Row,
  Text,
  Tabs,
  TextLink,
  Spacing,
  withTheme,
  useTheme,
  useDevice
} from '@dls/web';
import { getUrlParams, setUrlParams, getCookie } from '@lux/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { trans as t } from '../../helpers/localisation';
import useThirdPartyIndicator from '../../hooks/useThirdPartyIndicator';
import {
  authActions,
  planActions,
  rootActions,
  planHelpers,
  eshopUserActions
} from '@detox/actions';
import { navigate } from 'gatsby';
import Theme from '../../theme';
import CisPointOfEntry from '../CisPointOfEntry';
import SelectionModal from '../SelectionModal';
import TransactionDialog from '../TransactionDialog';
import { KeyValue } from '../../types/common.types';
import CisError from '../../components/CisError';
import {
  cisGOMOPortInEntryUrl,
  plansTermsAndConditionsUrl
} from '../../config/links';
import {
  flattenNodes,
  getAemConfigs,
  handlePromotionPlanNavigation
} from '../../helpers/common';
import isMobileSharePlan from '../../helpers/is-mobile-share-plan';
import isSimOnlyPlan from '../../helpers/is-sim-only-plan';
import slugify from '../../helpers/slugify';
import htmlStringToReactComponent from '../../helpers/htmlStringToReactComponent';
import { navigateToSpringDPlans } from '../../helpers/navigation';
import SimOnlyPlusUpSell from './SimOnlyPlusUpSell';
import { CATALOGUE } from '../../constants';
import { TOMO_INDICATORS } from '../../constants/third-party';
import {
  getPhonesWithPrice,
  getUrlByPlan,
  sortByRankingAndTitle,
  constructProductUrl,
  getPhoneCatalogBanner
} from '../../helpers/catalogue-helpers';
import {
  ACTION_TYPES,
  ACTION_TYPES as APP_ACTION_TYPES
} from '../../constants/actions';
import { setIppFilter } from '../../reducers/productCatalogs';
import { APP_TYPE_ANY } from '../../types/common.types';
import { getUIAMLoginUrl } from '../../config';
import { navigation } from '../../middlewares/navigation-constants';
import { clearSpecialPromo, setSpecialPromo } from '../../reducers/promotions';
import { AEM_CONFIG_KEYS } from '../../config/common';
import { featureFlagType } from '../../types';
import isFeatureFlagEnabled from '../../helpers/feature-flags';
import { BannerList } from '../RrpBanner';
import { BANNER_FLOW_TYPE } from '../../types/plan.types';
import { NetworkSpeedMap } from '../../config/plan-group-config';
import PricePlanCards from './PricePlanCards';
import CONSTANT from '../../constants/common';
import CONSTANTS from '../../constants/common';
import { AbFeatureNames } from '../../constants/ab-feature-names';
import { usePromotionPricing } from '../../hooks/useOfferPlanPromotions';
import { setErrorData } from '../../reducers/error';
import notifications from '../../config/notifications';

interface Data {
  allPlan: APP_TYPE_ANY[];
  allPhone: APP_TYPE_ANY[];
  allPlanGroup: APP_TYPE_ANY[];
  allAemPages: APP_TYPE_ANY[];
  allCis: APP_TYPE_ANY;
  allVariableConfig: APP_TYPE_ANY;
  allPhoneBanner: APP_TYPE_ANY[];
  allErrorMessage: APP_TYPE_ANY[];
}

interface Props {
  data: Data;
  location: WindowLocation;
  isCisPage?: boolean;
}

export const PlanCatalog: React.FC<Props> = ({
  data,
  location,
  isCisPage = false
}) => {
  const { theme } = useTheme();
  const { hasIndicator: hasTomoIndicator } = useThirdPartyIndicator(
    TOMO_INDICATORS
  );
  const { getAuthToken, resetWorryFree } = authActions;
  const dispatch = useDispatch();
  const { auth, selectedPlan, cisUser, cisPlanRates, promotions } = useSelector(
    (state: KeyValue) => ({
      auth: state.auth,
      selectedPlan: state.plan?.selectedPlan,
      cisUser: state.user?.cis?.information,
      cisPlanRates: state.user?.cis?.information?.rates,
      promotions: state.promotions
    })
  );
  const { isMobile } = useDevice();

  const activePromotion = promotions?.activePromotion;
  const promotionsPlansList = promotions?.promotionsPlansList;

  const {
    isFeatureAllowed: [abTestingEshopLogoutEnabled]
  } = useABTesting({
    featureName: [AbFeatureNames.ESHOP_LOGOUT]
  });

  useEffect(() => {
    dispatch(rootActions.resetByKeys(['product', 'user']));
    dispatch({
      type: APP_ACTION_TYPES.ORDER.RESET_ORDER
    });
    dispatch({ type: APP_ACTION_TYPES.DELIVERY.CLEAR_DELIVERY });
    dispatch(clearSpecialPromo());
    dispatch(setErrorData(flattenNodes(data.allErrorMessage)));
  }, []);

  useEffect(() => {
    const cleanWorryFree = auth?.worryFree?.otpVerified;

    if (cleanWorryFree) {
      dispatch(getAuthToken({ cleanWorryFree }));
    } else if (auth?.worryFree?.status) {
      dispatch(resetWorryFree());
    }
  }, [auth?.worryFree?.otpVerified, auth?.worryFree?.status]);

  const [openModal, setOpenModal] = useState(false);
  const [openMSModal, setOpenMSModal] = useState(false);
  const [
    isSimOnlyPlusUpSellFeatureEnabled,
    setIsSimOnlyPlusUpSellFeatureEnabled
  ] = useState(false);
  const [showSimOnlyPlusUpSell, setShowSimOnlyPlusUpSell] = useState(false);
  const allCis = data.allCis?.nodes;

  const isCisPlanCatalogue = Boolean(cisPlanRates) && Boolean(allCis);

  let { allPlanGroup, allPhone, allPhoneBanner } = data;
  const { allVariableConfig } = data;

  const variableConfig = useMemo(() => flattenNodes(allVariableConfig), [
    allVariableConfig
  ]);
  const [
    networkSpeedMap = NetworkSpeedMap,
    promotionPricingConfig
  ] = getAemConfigs(variableConfig, [
    AEM_CONFIG_KEYS.DETOX_NETWORK_SPEED_MAP,
    AEM_CONFIG_KEYS.SPECIAL_OFFER_PRICING
  ]);

  allPhone = useMemo(() => flattenNodes(allPhone), [allPhone]);
  allPlanGroup = useMemo(() => flattenNodes(allPlanGroup), [allPlanGroup]);
  allPhoneBanner = flattenNodes(allPhoneBanner);
  const flattenAemPlans = flattenNodes(data.allPlan);
  const allPlan = useMemo(() => {
    const plans = planHelpers.retrievePlanList({
      aemPlans: flattenAemPlans,
      isCisPlanCatalogue,
      cisPlanRates
    });
    return plans.filter(plan => plan.enabledOnPlanCatalog);
  }, [data.allPlan, cisPlanRates]);

  const [selectedPlanGroupName, setSelectedPlanGroup] = useState<string>();

  const urlParams = getUrlParams(location.search);
  const offerMode = urlParams?.offer_mode as string;
  usePromotionPricing(promotionPricingConfig, offerMode);

  const planGroupOptions = useMemo(() => {
    let planGroupNames = [...allPlan];

    if (hasTomoIndicator) {
      planGroupNames = planGroupNames.filter(plan => !isMobileSharePlan(plan));
    }

    planGroupNames = planGroupNames.map(plan => plan.groupName);

    return allPlanGroup
      .filter(group => planGroupNames.includes(group.groupName))
      .map(group => {
        const { groupName, groupDisplayName, shortDescription } = group;

        return {
          ...group,
          text: groupDisplayName || groupName,
          icon: null,
          value: groupName,
          description: shortDescription
        };
      });
  }, [allPlanGroup, allPlan, hasTomoIndicator]);

  const selectedPlanGroup =
    planGroupOptions.find(pg => pg.groupName === selectedPlanGroupName) ||
    planGroupOptions[0];

  const plansForGroup = allPlan.filter(
    plan => plan?.groupName === selectedPlanGroup?.groupName
  );

  const { renderPlans, selectedPlanWithDiscount } = useMemo(() => {
    const applyPromotionPricing = plan => {
      if (
        (activePromotion || offerMode) &&
        !isCisPlanCatalogue &&
        promotionsPlansList?.length
      ) {
        const promotionPrice = promotionsPlansList.find(
          pp => pp.planId === plan.planId
        );
        if (promotionPrice) {
          return {
            ...plan,
            discountedPrice: promotionPrice.price
          };
        }
      }
      return plan;
    };

    // Apply promotion pricing to plansForGroup
    const processedRenderPlans =
      (activePromotion || offerMode) &&
      !isCisPlanCatalogue &&
      promotionsPlansList?.length
        ? plansForGroup.map(applyPromotionPricing)
        : plansForGroup;

    // Apply promotion pricing to selectedPlan
    const processedSelectedPlanWithDiscount = selectedPlan
      ? applyPromotionPricing(selectedPlan)
      : selectedPlan;

    return {
      renderPlans: processedRenderPlans,
      selectedPlanWithDiscount: processedSelectedPlanWithDiscount
    };
  }, [
    plansForGroup,
    selectedPlan,
    activePromotion,
    offerMode,
    isCisPlanCatalogue,
    promotionsPlansList
  ]);

  const allSimOnlyPlusPlan = allPlan.filter(({ groupName }) =>
    groupName
      .toUpperCase()
      .includes(CATALOGUE.PLANS_GROUP_NAME.SIM_ONLY_PLUS.toUpperCase())
  );

  useEffect(() => {
    const urlParams = getUrlParams(location.search);
    if (urlParams) {
      // Find the plan that matches the query string
      const matchedPlan = allSimOnlyPlusPlan.find(({ planName }) => {
        if (urlParams?.utm_plan) {
          return planName
            .toUpperCase()
            .includes(urlParams?.utm_plan.toUpperCase());
        }
        return urlParams?.utm_plan === planName;
      });

      const matchMobileSharePlan = flattenAemPlans.find(({ planRelId }) => {
        if (
          urlParams?.plan_id &&
          urlParams?.group === 'mobile-share' &&
          urlParams?.code
        ) {
          return planRelId && planRelId === urlParams?.plan_id;
        }
      });

      if (matchMobileSharePlan) {
        dispatch(
          planActions.setSelectedPlan({
            ...matchMobileSharePlan,
            mobileShareOrderType: 'MS_NEW'
          })
        );
        dispatch(
          setSpecialPromo({
            promoCode: urlParams?.code,
            planIds: [matchMobileSharePlan?.planId],
            boIds: []
          })
        );
      }

      if (matchedPlan && urlParams?.utm_skipdevice === 'true') {
        dispatch(planActions.setSelectedPlan(matchedPlan));
        setSelectedPlanGroup(matchedPlan?.groupName);
        setShowSimOnlyPlusUpSell(true);
      }
    }
  }, []);

  // Add the variants that contain installment prices to the phones array
  const phones = allPhone
    .filter(phone => !(phone.isRoi === true || phone.groupCampaignId))
    .map(phone => {
      const { variants } = phone;

      // Get all the plans that are part of the Sim Only Plus group
      const simOnlyPlusPlans = allSimOnlyPlusPlan.map(({ mecId }) => mecId);

      // Get the variants that are only part of the Sim Only Plus plans
      const simOnlyPlusVariants = variants.filter(({ planId }) =>
        simOnlyPlusPlans.includes(planId)
      );

      return {
        ...phone,
        variants: simOnlyPlusVariants
      };
    });

  const simOnlyPlanWithDefaultMonthlyTerm = {
    ...selectedPlan,
    selectedMonthlyTerm: CATALOGUE.INSTALLMENT_MONTHS[0]
  };

  const phonesWithPrice = getPhonesWithPrice(
    phones,
    simOnlyPlanWithDefaultMonthlyTerm
  );

  const sortedPhones = phonesWithPrice.sort(sortByRankingAndTitle);

  useEffect(() => {
    const { group, code } = getUrlParams(location.search);

    if (location.search) {
      // Find the plan group that matches the query string
      // Prioritize URL group parameter over selectedPlan
      let matchedPlanGroup = null;

      if (group) {
        // Helper function to normalize group names for comparison
        const normalizeGroupName = (name: string) =>
          name
            .toLowerCase()
            .replace(/_/g, '-')
            .replace(/\s+/g, '-');

        const normalizedUrlGroup = normalizeGroupName(group);

        // Try exact match with groupName (case-insensitive, normalized)
        matchedPlanGroup = planGroupOptions.find(
          ({ groupName }) =>
            normalizedUrlGroup === normalizeGroupName(groupName)
        );

        // If no exact match, try slugified match (case-insensitive, normalized)
        if (!matchedPlanGroup) {
          matchedPlanGroup = planGroupOptions.find(
            ({ groupName }) =>
              normalizedUrlGroup === normalizeGroupName(slugify(groupName))
          );
        }
      }

      // Fallback to selectedPlan if no URL group parameter match
      if (!matchedPlanGroup && selectedPlan?.groupName) {
        matchedPlanGroup = planGroupOptions.find(({ groupName }) =>
          groupName.toUpperCase().includes(selectedPlan.groupName.toUpperCase())
        );
      }

      if (matchedPlanGroup) {
        setSelectedPlanGroup(matchedPlanGroup.groupName);
      } else {
        // Set the first plan group as default if no matching plan group from the url query string
        setSelectedPlanGroup(planGroupOptions[0]?.groupName);
      }

      if (
        isMobileSharePlan(selectedPlan) &&
        promotions?.specialPromo?.promoCode === code
      ) {
        if (!code || code !== CONSTANTS.BUNDLED_MOBILESHARE) return;

        setOpenMSModal(false);
        const uiamAccessToken = getCookie(CONSTANT.UIAM_ACCESS_TOKEN);
        if (!uiamAccessToken) {
          const url = setUrlParams(navigation.CHOOSE_NUMBER_PAGE, {
            plan_id: selectedPlan?.planRelId,
            code: code,
            group: group
          });
          window.open(getUIAMLoginUrl(url), '_self');
        } else {
          navigate(`/${navigation.CHOOSE_NUMBER_PAGE}`, { replace: true });
        }
      }
    } else {
      const defaultPlan =
        selectedPlan || allPlan.find(({ selected }) => selected) || allPlan[0];
      setSelectedPlanGroup(defaultPlan.groupName);
    }
  }, [allPlan, location.search, planGroupOptions, selectedPlan]);

  useEffect(() => {
    const { group } = getUrlParams(location.search);
    const cisCondition = !isCisPage || (isCisPage && cisPlanRates?.length);

    if (selectedPlanGroupName && cisCondition) {
      // Helper function to normalize group names for comparison
      const normalizeGroupName = (name: string) =>
        name
          .toLowerCase()
          .replace(/_/g, '-')
          .replace(/\s+/g, '-');

      const normalizedSelectedGroup = normalizeGroupName(
        slugify(selectedPlanGroupName)
      );
      const normalizedUrlGroup = normalizeGroupName(group || '');

      // Only update URL if the normalized groups don't match
      if (normalizedSelectedGroup !== normalizedUrlGroup) {
        updateQueryFilters(location, { group: slugify(selectedPlanGroupName) });
      }
    }
  }, [selectedPlanGroupName, isCisPage, cisPlanRates]);

  const isSimOnlyPlusUpsellDesignFlag = isFeatureFlagEnabled(
    featureFlagType.SIMONLYPLUS_UPSELL_DESIGN
  );

  // SIM Only Plus UpSell feature will be enabled only if both
  // feature flag is enabled in environment variables and
  // AEM ipp upsell indicator is enabled.
  // SOP upsell feature will be displayed for both RES and CIS flow.
  useEffect(() => {
    if (selectedPlanGroup) {
      const enableSimOnlyPlusUpSellFeature =
        isSimOnlyPlusUpsellDesignFlag &&
        (isCisPlanCatalogue
          ? selectedPlanGroup.enableIppForCIS
          : selectedPlanGroup.enableIppForRES);
      setIsSimOnlyPlusUpSellFeatureEnabled(enableSimOnlyPlusUpSellFeature);

      //showing upsell section if upsell plan selected on browse back setShowSimOnlyPlusUpSelland page load
      if (
        selectedPlan &&
        isSimOnlyPlan(selectedPlan) &&
        enableSimOnlyPlusUpSellFeature
      ) {
        setShowSimOnlyPlusUpSell(true);
        window.scrollTo(0, 0);
      }
    }
  }, [isSimOnlyPlusUpsellDesignFlag, isCisPlanCatalogue, selectedPlanGroup]);

  const handleTabChange = (planGroups, index) => {
    const { groupName } = planGroups[index];
    setSelectedPlanGroup(groupName);
    setShowSimOnlyPlusUpSell(false);
    //Resets the selected Plan
    dispatch(planActions.setSelectedPlan(undefined));
  };

  const hideSimOnlyPlusUpSellScreen = () => {
    setShowSimOnlyPlusUpSell(false);
  };

  const navigateProductDetailsPage = phone => {
    const { variants, slug } = phone;

    const url = constructProductUrl(
      variants[0]?.color,
      variants[0]?.size,
      selectedPlan.planName
    );
    dispatch({
      type: ACTION_TYPES.PLAN.EXCLUSIVE_VALIDATION_HIDE
    });
    return navigate(`/phones/${slug}/${url}`, {
      state: {
        isChangePlan: true
      }
    });
  };

  /**
   * Navigate to phone catalog page and filter for IPP devices
   */
  const showEligibleDevicesForSimOnlyPlus = () => {
    const planQueryParam = getUrlByPlan({
      ...selectedPlan,
      selectedMonthlyTerm: CATALOGUE.INSTALLMENT_MONTHS[0]
    });

    const paramItems = [
      planQueryParam,
      `${CATALOGUE.QUERYSTRING_TYPE.IPP}=${CATALOGUE.QUERY_PLANS.IPP_TEXT}`
    ];
    return navigate(`/${paramItems.join('&')}`);
  };

  const proceedSimOnlyPlusWithoutDeviceFlow = () => {
    dispatch({
      type: ACTION_TYPES.USER.SET_USER_SELECTION,
      payload: { plan: selectedPlanWithDiscount }
    });

    handlePromotionPlanNavigation({
      promotions,
      selectedPlan,
      navigate,
      onOpenModal: setOpenModal
    });
  };

  const handlePlanClick = plan => {
    dispatch(planActions.setSelectedPlan(plan));

    // Avoid setting userSelection for SIM Only Plus Plans when going through Upsell screen,
    // Because it should only be set later once user confirms inside UpSell screen
    if (!isSimOnlyPlan(plan) || !isSimOnlyPlusUpSellFeatureEnabled) {
      dispatch(setIppFilter(false));
      dispatch({
        type: ACTION_TYPES.USER.SET_USER_SELECTION,
        payload: { plan }
      });
    }

    if (isSimOnlyPlan(plan)) {
      if (isSimOnlyPlusUpSellFeatureEnabled) {
        setShowSimOnlyPlusUpSell(true);
      } else {
        handlePromotionPlanNavigation({
          promotions,
          selectedPlan,
          navigate,
          onOpenModal: setOpenModal
        });
      }
    } else if (isMobileSharePlan(plan)) {
      setOpenMSModal(true);
    } else {
      if (isCisPlanCatalogue) {
        const { sessionToken } = getUrlParams(location.search);
        const newPath = setUrlParams('/cis-phones', {
          sessionToken,
          plan: slugify(plan.planName),
          ...(plan.ipp ? { ipp: slugify(plan.ipp) } : {})
        });
        window.location.assign(newPath);
        return;
      }
      return navigate('/');
    }
  };

  const handleMobileShareClick = async flow => {
    dispatch(
      planActions.setSelectedPlan({
        ...selectedPlan,
        mobileShareOrderType: flow
      })
    );
    setOpenMSModal(false);

    if (abTestingEshopLogoutEnabled) {
      await dispatch(eshopUserActions.eshopLogout({ mode: 'retainJourney' }));
    }
    // To make sure that Eshop clear httpOnly cookie properly before navigating
    setTimeout(() => {
      return window.open(
        getUIAMLoginUrl(navigation.CHOOSE_NUMBER_PAGE),
        '_self'
      );
    }, 500);
  };

  const handleGomoPortInClick = () => {
    if (!isCisPlanCatalogue) {
      navigateToSpringDPlans({ groupName: selectedPlanGroup.groupName });
    } else {
      window.location.assign(cisGOMOPortInEntryUrl);
    }
  };

  const renderHeader = () => {
    const cisCompanyName = cisUser?.parentCompanyName;

    if (!isCisPlanCatalogue) {
      return (
        <Text type="pageTitle" tag="h1">
          {t('PLANS_FOR_EVERYONE')}
        </Text>
      );
    }

    return (
      <Row>
        <Column sm={12} lg={8}>
          <Text type="pageTitle">{`Postpaid Plans for ${cisCompanyName}`}</Text>
          <Spacing top={2}>
            <Text type="body">{t('CORPORATE_INDIVIDUAL_SCHEME_SUBTITLE')}</Text>
          </Spacing>
        </Column>
      </Row>
    );
  };

  const findSelectedTabIndex = (allPlanGroups, selectedPlanGroup) => {
    const index = allPlanGroups.findIndex(
      item => item.groupName === selectedPlanGroup
    );
    return index !== -1 ? index : null;
  };

  const renderPlanGroupTabs = () => {
    const addTitleKey = dataArray => {
      return dataArray.map(item => ({ ...item, title: item.text }));
    };
    const planGroups = addTitleKey(planGroupOptions);

    return (
      <Tabs
        tabs={planGroups}
        selected={
          selectedPlanGroupName &&
          findSelectedTabIndex(planGroups, selectedPlanGroupName)
        }
        onTabItemClick={(e, index) => handleTabChange(planGroups, index)}
      />
    );
  };

  const handleDataProtectionLinkClick = () => {
    window.open(plansTermsAndConditionsUrl, '_blank', 'noopener,noreferrer');
  };

  const renderPlanGroupFooterLiner = () => {
    const footerContent = selectedPlanGroup?.footerLiner
      ? htmlStringToReactComponent(selectedPlanGroup?.footerLiner)
      : t('PLANS_FOOTER_TERMS_AND_CONDITIONS', {
          tnc: (
            <TextLink
              type="smallBody"
              data-testid="terms-link"
              onClick={handleDataProtectionLinkClick}
            >
              Terms and Condition
            </TextLink>
          ) as APP_TYPE_ANY
        });

    return <Text color={theme.cl_ter_l1}>{footerContent}</Text>;
  };

  const renderBanner = () => {
    if (allPhoneBanner) {
      const flowType = isCisPage
        ? BANNER_FLOW_TYPE.CIS_PLAN
        : BANNER_FLOW_TYPE.RES_PLAN;
      const promoBanner = getPhoneCatalogBanner(allPhoneBanner, flowType);
      return promoBanner ? <BannerList banners={promoBanner} /> : <></>;
    }
  };

  const renderCisPointOfEntry = () => {
    return (
      <Spacing top={isMobile ? 2 : -4}>
        <CisPointOfEntry />
      </Spacing>
    );
  };

  if (cisPlanRates?.length && !allPlan?.length) {
    const cisError = notifications.CIS_SESSION_TOKEN_EXPIRED;
    return (
      <CisError
        title={cisError.title as string}
        subTitle={cisError.subTitle as string}
        text={cisError.text as string}
        ctaText={cisError.ctaText as string}
        ctaUrl={cisError.ctaUrl as string}
      />
    );
  }

  return (
    <>
      {renderBanner()}
      {!isCisPlanCatalogue ? renderCisPointOfEntry() : null}
      <Grid>
        <Row>
          <Column xs={12} sm={12} lg={12} noGutter>
            <StyledRowMargin>
              <Spacing top={isMobile ? 1 : 4}>{renderHeader()}</Spacing>
            </StyledRowMargin>
            {planGroupOptions?.length > 1 && (
              <Spacing bottom={isMobile ? 0 : 1}>
                <Column md={12} id="plan-tabs">
                  {renderPlanGroupTabs()}
                </Column>
              </Spacing>
            )}
            {!showSimOnlyPlusUpSell && (
              <Grid>
                <StyledRow>
                  <PricePlanCards
                    plansData={renderPlans}
                    isCisPlanCatalogue={isCisPlanCatalogue}
                    networkSpeedMap={networkSpeedMap}
                    onPlanClick={plan => handlePlanClick(plan)}
                  />
                </StyledRow>
                <Spacing top={isMobile ? 2 : 0} bottom={isMobile ? 2 : 6}>
                  {renderPlanGroupFooterLiner()}
                </Spacing>
              </Grid>
            )}
            {showSimOnlyPlusUpSell && (
              <Grid>
                <StyledRow>
                  <StyledColumn sm={12} md={12}>
                    <SimOnlyPlusUpSell
                      networkSpeedMap={networkSpeedMap}
                      phones={sortedPhones}
                      hideSimOnlyPlusUpSellScreen={hideSimOnlyPlusUpSellScreen}
                      proceedSimOnlyPlusWithoutDeviceFlow={
                        proceedSimOnlyPlusWithoutDeviceFlow
                      }
                      showEligibleDevicesForSimOnlyPlus={() => {
                        dispatch(setIppFilter(true));
                        dispatch({
                          type: ACTION_TYPES.USER.SET_USER_SELECTION,
                          payload: { plan: selectedPlanWithDiscount }
                        });
                        showEligibleDevicesForSimOnlyPlus();
                      }}
                      navigateProductDetailsPage={navigateProductDetailsPage}
                      selectedPlanGroup={selectedPlanGroup}
                      selectedPlan={selectedPlanWithDiscount}
                    />
                  </StyledColumn>
                </StyledRow>
              </Grid>
            )}

            <TransactionDialog
              open={openModal}
              onClickGomoPortIn={handleGomoPortInClick}
              isGomoToSingtelEnabled={isSimOnlyPlan(selectedPlan)}
              onClose={() => setOpenModal(false)}
              data-testid="transaction-selection"
            />
            <SelectionModal
              open={openMSModal}
              onClose={() => setOpenMSModal(false)}
              data={{
                lhs: {
                  title: t('HAVE_MOBILESHARE_LINE') as string,
                  buttons: [
                    {
                      buttonText: t('BUTTON_RECONTRACT') as string,
                      onButtonClick: () =>
                        handleMobileShareClick('MS_RECONTRACT')
                    }
                  ]
                },
                rhs: {
                  title: t('WANT_MOBILESHARE_LINE') as string,
                  buttons: [
                    {
                      buttonText: t('CHOOSE_MAINLINE') as string,
                      onButtonClick: () => handleMobileShareClick('MS_NEW')
                    }
                  ]
                }
              }}
            />
          </Column>
        </Row>
      </Grid>
    </>
  );
};

const updateQueryFilters = (location, { group }) => {
  if (!location) return;
  const queryParams = getUrlParams(location.search);
  const newUrl = setUrlParams(location.pathname, {
    ...queryParams,
    group
  });
  return navigate(newUrl, { replace: true });
};

export default PlanCatalog;

const StyledRow = withTheme(styled(props => <Row {...props} />)`
  ${({ coreTheme }) => css`
    display: flex;
    margin: 0 -8px;
    @media (min-width: ${coreTheme.brk_md}) {
      margin-bottom: ${Theme.spacing(2)};
    }
  `}
`);

const StyledRowMargin = withTheme(styled(props => <Row {...props} />)`
  ${({ coreTheme }) => css`
    display: flex;
    margin: 0 12px;
    @media (min-width: ${coreTheme.brk_md}) {
      margin-bottom: ${Theme.spacing(2)};
    }
  `}
`);

const StyledColumn = styled(Column)`
  display: table-cell;
  margin-bottom: ${Theme.spacing(3)};
`;
